import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Badge } from '@/components/ui/PageHeader';
import { SelectField } from '@/components/ui/FormFields';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/context/AuthContext';
import {
  listStickers,
  copyStickerToBoard,
  type NostalgiaSticker,
  type NostalgiaEra,
  ERA_OPTIONS,
} from '@/api/nostalgia';

const STICKER_CATEGORY_OPTIONS = [
  { value: 'music', label: 'Music' },
  { value: 'food', label: 'Food' },
  { value: 'sports', label: 'Sports' },
  { value: 'tv', label: 'TV' },
  { value: 'school', label: 'School' },
  { value: 'games', label: 'Games' },
  { value: 'festivals', label: 'Festivals' },
  { value: 'misc', label: 'Miscellaneous' },
];

export default function StickersList() {
  const { isAuthenticated } = useAuth();

  const [stickers, setStickers] = useState<NostalgiaSticker[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eraFilter, setEraFilter] = useState('');

  // Copy-to-board: the board a sticker goes onto, and what the server said.
  const [boardId, setBoardId] = useState('');
  const [copyNote, setCopyNote] = useState<string | null>(null);
  // The last copy and its retry key, to prove a retry places nothing new.
  const [lastCopy, setLastCopy] = useState<{ boardId: string; sticker: NostalgiaSticker; key: string; itemId: string } | null>(null);

  const copy = async (sticker: NostalgiaSticker) => {
    if (!boardId.trim()) {
      setCopyNote('Enter a board id first');
      return;
    }
    try {
      const key = crypto.randomUUID();
      const res = await copyStickerToBoard(boardId.trim(), sticker.id, { positionX: 0.1, positionY: 0.1, idempotencyKey: key });
      setLastCopy({ boardId: boardId.trim(), sticker, key, itemId: res.data.id });
      setCopyNote(`"${sticker.name}" is on the board as item ${res.data.id}`);
    } catch (error) {
      setCopyNote('✘ ' + (error instanceof Error ? error.message : 'failed'));
    }
  };

  const retryLastCopy = async () => {
    if (!lastCopy) return;
    try {
      const res = await copyStickerToBoard(lastCopy.boardId, lastCopy.sticker.id, {
        positionX: 0.1,
        positionY: 0.1,
        idempotencyKey: lastCopy.key,
      });
      setCopyNote(
        res.data.id === lastCopy.itemId
          ? 'Same key, same sticker: nothing new was placed.'
          : 'A second sticker was placed. That is the bug the key exists to stop.',
      );
    } catch (error) {
      setCopyNote('✘ ' + (error instanceof Error ? error.message : 'failed'));
    }
  };

  const fetchStickers = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const response = await listStickers({
        category: categoryFilter || undefined,
        era: eraFilter as NostalgiaEra || undefined,
      });
      setStickers(response.data);
    } catch (error) {
      console.error('Failed to fetch stickers:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, categoryFilter, eraFilter]);

  useEffect(() => {
    fetchStickers();
  }, [fetchStickers]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">Nostalgia Stickers</h1>
            <AuthButton />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please login to view stickers.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title="Nostalgia Stickers"
            subtitle="Decorative stickers for boards"
            breadcrumbs={[
              { label: 'Nostalgia', href: '/lab/nostalgia' },
              { label: 'Stickers' },
            ]}
          />
          <AuthButton />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <SelectField
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                options={STICKER_CATEGORY_OPTIONS}
                placeholder="All Categories"
              />
            </div>
            <div className="w-40">
              <SelectField
                label="Era"
                value={eraFilter}
                onChange={(e) => setEraFilter(e.target.value)}
                options={ERA_OPTIONS}
                placeholder="All Eras"
              />
            </div>
          </div>
        </div>

        {/* Stickers Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : stickers.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center text-gray-500">
            No stickers found. Stickers are seeded via database.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Copy-to-board: one call puts a sticker on a board, carrying its address and name. */}
            <div className="col-span-full flex items-center gap-2 text-xs text-gray-600">
              <input
                className="border border-gray-300 rounded-lg px-2 py-1 text-xs w-80"
                placeholder="board id to copy onto"
                value={boardId}
                onChange={(e) => setBoardId(e.target.value)}
              />
              {lastCopy && (
                <button className="border border-gray-300 rounded-lg px-2 py-1" onClick={retryLastCopy}>
                  Retry last copy with its key
                </button>
              )}
              {copyNote && <span>{copyNote}</span>}
            </div>
            {stickers.map((sticker) => (
              <div
                key={sticker.id}
                className="bg-white rounded-lg border p-4 text-center hover:shadow-md transition-shadow"
              >
                <img
                  src={sticker.imageUrl}
                  alt={sticker.name}
                  className="w-16 h-16 mx-auto object-contain mb-2"
                />
                <div className="text-sm font-medium text-gray-900 truncate">
                  {sticker.name}
                </div>
                <div className="flex justify-center gap-1 mt-2">
                  <Badge variant="gray">{sticker.category}</Badge>
                  {sticker.era && (
                    <Badge variant="purple">{sticker.era.replace('era_', '')}</Badge>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-1">{sticker.stickerId}</div>
                <button
                  type="button"
                  className="mt-2 text-xs text-blue-600 hover:underline"
                  onClick={() => copy(sticker)}
                >
                  copy to board
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">Adding Stickers</h3>
          <p className="text-sm text-blue-800">
            Stickers are added via seed scripts. To add new stickers, update the seed file and run:
          </p>
          <code className="block mt-2 text-xs bg-blue-100 p-2 rounded">
            cd apps/api && pnpm prisma db seed
          </code>
        </div>
      </div>
    </div>
  );
}
